// Growem - Sesión ("modo FOCO"). Live workout for today; read-only detail for past days;
// rest card for Sunday. Date navigator with a custom Monday-first calendar popup.
//
// Sesión movible: la rutina que se pinta es la EFECTIVA del día (domain/planDay.planIdxOf), no la
// que dicta el día de la semana. Esta pantalla es la única que la escribe (selector de rutina), y
// todo lo que cuelga del índice (ejercicios, claves de series, historial, pesos) se re-dibuja con
// él, así que un cambio de rutina limpia el estado local que pertenecía a la rutina abandonada.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from '../state/store'
import { useUI } from '../state/ui'
import { planForDate, CAL_HEADERS, DOWS, prepForDate, cooldownForDate, type PrepItem } from '../data/plan'
import type { Exercise } from '../types'
import {
  setKey, pairKey, weightKey, sessionProgress, exComplete, dayCounts,
  normalSetsCount, cascadeWeightKeys, allNormalSetsDone,
} from '../domain/rules'
import { buildHistoryIndex } from '../lib/strength'
import { parseCapture, clampStopwatchSeconds, type CaptureField } from '../domain/capture'
import { resolveTarget, targetPlaceholders } from '../domain/target'
import { currentBodyweight } from '../domain/strengthStandards'
import { addDays, dateLabel, dow, planIndexForDow, monthGrid, monthTitle, parseDay } from '../domain/dates'
// Sesión movible: una sola fórmula de índice efectivo en todo el repo (planIdxOf) y las reglas de
// cambio (bloqueo por series, estado de la semana, rutina de ayer recuperable) como funciones puras.
import {
  planIdxOf, isMovedSession, planDayWeekday, planDayLabel, dayHasSessionData,
  weekPlanStatus, recoverableYesterday, applyPlanDay, type PlanDayWeekStatus,
} from '../domain/planDay'
import { restBand, zoneOf } from '../domain/rest'
import ConfirmSheet from './ConfirmSheet'

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

type Field = 'w' | 'r' | 'rir'

/**
 * Marcas de preparación/cierre que sobreviven a un cambio de rutina, emparejadas POR ETIQUETA.
 *
 * POR QUÉ ESTO EXISTE (Fase 4, MUST_FIX del selector de rutina): las claves de estas listas son
 * POSICIONALES (`prep-0`, `post-3`), pero las listas son POR RUTINA. Al cambiar de rutina el mismo
 * índice pasa a señalar OTRO paso: `prep-1` de Pull A es "Band pull-aparts x20" y `prep-1` de Push A
 * es "⚠ Manguito rotador (hombro izq.)". Dejar la marca donde estaba afirma un paso de seguridad que
 * nadie hizo, y en una rutina más corta las claves sobrantes quedan huérfanas y viajan a la base.
 *
 * Reparto de responsabilidades (decisión de la auditoría, dos piezas que encajan):
 *  1. el DOMINIO (applyPlanDay) borra TODAS las claves posicionales de la rutina abandonada, que es
 *     lo único seguro sin conocer las listas;
 *  2. esta pantalla, que sí conoce las listas, vuelve a marcar las que existen igual (mismo `label`)
 *     en la rutina nueva, porque ese paso SÍ se hizo de verdad y sí sigue en la lista de hoy.
 *
 * Devuelve las CLAVES A CONMUTAR (toggleSet) para pasar de `base` (el día tal y como queda tras
 * applyPlanDay) al estado deseado, así el resultado es correcto tanto si el dominio limpió como si
 * no: si limpió, sale la lista de marcas a re-poner; si no limpiara, saldrían además las posicionales
 * huérfanas para quitarlas. Nunca conmuta una clave que ya esté como debe (un toggle de más la
 * apagaría, que es justo el fallo que se quiere evitar).
 *
 * El emparejamiento es 1 a 1: una etiqueta repetida en la lista nueva no recibe dos marcas viejas.
 */
function togglesDeMarcas(
  doneAntes: Record<string, 1>,
  doneBase: Record<string, 1>,
  prefijo: 'prep' | 'post',
  listaVieja: PrepItem[],
  listaNueva: PrepItem[],
): string[] {
  const deseado = new Set<string>()
  const usados = new Set<number>()
  listaVieja.forEach((it, i) => {
    if (!doneAntes[`${prefijo}-${i}`]) return
    const j = listaNueva.findIndex((n, k) => !usados.has(k) && n.label === it.label)
    if (j === -1) return // ese paso no está en la rutina nueva: la marca se pierde, y así debe ser
    usados.add(j)
    deseado.add(`${prefijo}-${j}`)
  })
  const toggles: string[] = []
  for (const k of Object.keys(doneBase)) {
    if (k.startsWith(`${prefijo}-`) && !deseado.has(k)) toggles.push(k) // sobra: desmarcar
  }
  for (const k of deseado) {
    if (!doneBase[k]) toggles.push(k) // falta: marcar
  }
  return toggles
}

export default function Sesion() {
  const { getDay, today, toggleSet, setField, setNote, setTargetOverride, addSet, removeSet, cascadeWeight, setVariant, reorderSet, restoreSignal, setPlanDay, state, profile } = useStore()
  // `rest`/`discardRest`: al cambiar la rutina del día, el medidor de descanso que estuviera corriendo
  // apunta a una serie de la rutina abandonada (su clave lleva el índice dentro), así que se descarta.
  const { startRest, rest, discardRest } = useUI()

  const [viewedDate, setViewedDate] = useState<string>(today)
  const [calOpen, setCalOpen] = useState(false)
  const [calY, setCalY] = useState(() => parseDay(today).getFullYear())
  const [calM, setCalM] = useState(() => parseDay(today).getMonth())
  const [exIdx, setExIdx] = useState(0)
  const [rirHelp, setRirHelp] = useState(false)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  // Rejected captures, keyed EXACTLY like drafts (`${key}:${field}`). Lives ONLY in the UI: a value
  // outside the limits never reaches mutateDay (store.tsx:249-261), so it neither persists nor pushes
  // an undo entry (setField is the only pusher for these fields, store.tsx:323-337).
  // `show` splits MARKING from ANNOUNCING (a11y): the border + aria-invalid land on every keystroke,
  // but the TEXT only appears on blur, so a screen reader is not interrupted mid-typing (5 -> 51 -> 5).
  const [invalid, setInvalid] = useState<Record<string, { msg: string; show: boolean }>>({})
  const [prepOpen, setPrepOpen] = useState(false)
  const [postOpen, setPostOpen] = useState(false)
  const [variantOpen, setVariantOpen] = useState(false)
  // Drag-to-reorder sets: {from, dy} while a row is being dragged by its handle.
  const [dragSet, setDragSet] = useState<{ from: number; dy: number } | null>(null)
  // Un-marking a COMPLETED set needs explicit confirmation (an accidental tap must not silently
  // flip it back to pending). {key, body} while the sheet is open.
  const [confirmUndone, setConfirmUndone] = useState<{ key: string; body: string } | null>(null)
  // Per-set stopwatch for TIME-based exercises (farmer carry, planks): start on tap, stop writes the
  // elapsed seconds into the set's field. Wall-clock based; one at a time.
  const [crono, setCrono] = useState<{ key: string; start: number; now: number } | null>(null)
  // ---------- sesión movible (selector de rutina + aviso de recuperación) ----------
  // Los tres estados y las dos referencias viven AQUÍ, con el resto de hooks y por encima de los
  // returns tempranos (descanso e historia): el selector se pinta en dos ramas distintas y al
  // recuperar una rutina en domingo la pantalla salta de una rama a la otra sin desmontarse, así que
  // declararlos junto a su JSX rompería las reglas de los hooks.
  const [selectorOpen, setSelectorOpen] = useState(false)
  // Texto que se anuncia a un lector de pantalla tras cambiar de rutina. La región que lo lee está
  // montada SIEMPRE y vacía: una región viva que nace con texto no se anuncia en NVDA/VoiceOver.
  const [aviso, setAviso] = useState('')
  // El aviso de "ayer no entrenaste" se descarta solo en esta sesión de pantalla: no se persiste
  // (descartarlo no es un hecho del día, y mañana la situación puede ser otra).
  const [sugerenciaOff, setSugerenciaOff] = useState(false)
  // Destinos del foco tras cambiar de rutina: el disparador sobrevive con etiqueta nueva; el título
  // recibe el foco cuando el disparador desaparece (domingo que deja de ser descanso).
  const trigRef = useRef<HTMLButtonElement | null>(null)
  const h1Ref = useRef<HTMLDivElement | null>(null)
  // An undo/redo restored a day snapshot: local input drafts may now mask the restored values.
  // Clearing ALL drafts is the safe, simple rule (they are transient typing aids).
  // Rejections are cleared with them: they describe drafts that no longer exist.
  useEffect(() => { if (restoreSignal > 0) { setDrafts({}); setInvalid({}) } }, [restoreSignal])
  useEffect(() => {
    if (!crono) return
    const iv = window.setInterval(() => setCrono((c) => (c ? { ...c, now: Date.now() } : c)), 500)
    return () => window.clearInterval(iv)
  }, [crono?.key])

  // Per-exercise top-set history, rebuilt once per log change (NOT per keystroke): one linear pass
  // over the logged days. The target resolver reads this memo, so typing a set never rescans history.
  // Must sit with the hooks, ABOVE the isRest/isPast early returns (rules of hooks).
  const history = useMemo(() => buildHistoryIndex(state.days), [state.days])
  // Current bodyweight for the Tier-2c starting-weight estimate: latest logged weigh-in, else the
  // onboarding snapshot, else null (null -> no estimate, an honest first-time cue). Memoized with the
  // other hooks ABOVE the isRest/isPast early returns (rules of hooks).
  const currentBw = useMemo(() => currentBodyweight(state.days, profile.bodyweightKg), [state.days, profile.bodyweightKg])

  const d = getDay(viewedDate)
  // Índice EFECTIVO de la rutina del día: manda lo guardado y el calendario es solo el respaldo.
  // Antes esta línea derivaba el índice del día de la semana, así que una rutina movida se pintaba
  // con las claves de otra rutina (series en blanco, historial vacío). Re-keyea la pantalla entera:
  // ejercicios, claves de series, pesos objetivo y variantes cuelgan todos de `p`.
  const pIdx = planIdxOf(d)
  // Descanso = no hay rutina para ese día, que es lo que la rama de abajo quiere decir de verdad.
  // Reemplaza al viejo `isSunday`: hoy solo es cierto en domingo, pero así un domingo con una rutina
  // recuperada deja de ser descanso sin tocar una línea más.
  const isRest = pIdx == null
  // La rutina que toca por CALENDARIO en el día que se está viendo. Es el destino de "volver a la
  // rutina del <día>": en el store no hay un revert aparte, revertir es elegir este índice, y en
  // domingo vale null, que es exactamente el descanso.
  const idxDeHoy = planIndexForDow(dow(viewedDate))
  const isToday = viewedDate === today
  const isPast = viewedDate < today
  const nextDisabled = viewedDate >= today
  // Prechequeo del selector (misma regla que las variantes de ejercicio): con trabajo ya registrado,
  // cambiar de rutina mezclaría claves de dos rutinas en el mismo día y el progreso quedaría mal
  // contado. Los pasos de preparación y cierre NO bloquean el cambio (calentar no es entrenar), pero
  // sí cambian de sitio: sus claves son posicionales y las listas son por rutina, así que al cambiar
  // solo se conservan los pasos que también existen en la rutina nueva (ver togglesDeMarcas) y se
  // avisa antes para que nadie crea que se conserva todo.
  const bloqueado = dayHasSessionData(d)
  const soloPrep = !bloqueado && Object.keys(d.done).some((k) => k.startsWith('prep-') || k.startsWith('post-'))
  // Estado de las seis rutinas de la semana en curso: rotula cada opción del selector.
  const semana = weekPlanStatus(state.days, today)
  // Rutina de ayer que se puede recuperar hoy (null cuando no hay nada que ofrecer). La regla vive
  // en el dominio y está testeada; la pantalla solo decide si enseñar el aviso o no.
  const recuperable = isToday ? recoverableYesterday(state.days, today) : null

  // Nombre corto de una rutina ("Pull A"), sacado de la etiqueta del dominio ("Pull A (martes)")
  // quitando el día entre paréntesis: así el nombre visible tiene UNA sola fuente (domain/planDay) y
  // esta pantalla no repite la forma de recortar el nombre largo del plan.
  const nombreCorto = (dayIso: string, idx: number | null) => planDayLabel(dayIso, idx).replace(/\s*\([^)]*\)$/, '')

  // Cambiar la rutina del día. Único camino de escritura: lo usan el selector, el botón de volver a
  // la rutina del calendario y el aviso de recuperación.
  function elegirRutina(idx: number | null) {
    const eraDescanso = pIdx == null
    // Plan de re-marcado de preparación/cierre, calculado ANTES de escribir y contra el día tal y
    // como lo deja el dominio (applyPlanDay es pura: aquí solo se usa para saber con qué `done` se
    // va a quedar el día, no escribe nada). Se calcula antes porque después del cambio esta pantalla
    // ya no tiene la lista de la rutina abandonada a mano, y porque `getDay` todavía devolvería el
    // día viejo en este mismo tick (el store publica por setState).
    const base = applyPlanDay(d, idx)
    // `base === d` significa que CON EL DÍA QUE ESTA PANTALLA TIENE no había cambio que aplicar. Si
    // aun así el store dijera que sí lo hubo (el día que él ve es más fresco: otra pestaña, un
    // deshacer), el plan de re-marcado estaría calculado sobre un `done` que ya no existe, así que se
    // renuncia a él: perder las marcas es recuperable con un toque; re-marcar a ciegas dejaría
    // marcado un paso equivocado, que es el fallo que estamos cerrando.
    const marcas = base === d || pIdx == null || idx == null ? [] : [
      ...togglesDeMarcas(d.done, base.done, 'prep', prepForDate(viewedDate)[pIdx] ?? [], prepForDate(viewedDate)[idx] ?? []),
      ...togglesDeMarcas(d.done, base.done, 'post', cooldownForDate(viewedDate)[pIdx] ?? [], cooldownForDate(viewedDate)[idx] ?? []),
    ]
    const dec = setPlanDay(viewedDate, idx)
    if (!dec.ok) {
      // Carrera: entre abrir el selector y tocar la opción se marcó una serie (el store re-evalúa la
      // decisión contra el día más fresco). No se cierra el selector: en el re-dibujo el prechequeo
      // ya ve el día con series y pinta el motivo completo.
      setAviso(dec.reason === 'series'
        ? 'No se pudo cambiar: el día ya tiene series registradas.'
        : 'No se pudo cambiar de rutina.')
      return
    }
    // Solo cuando el cambio fue REAL: en un no-op el día no se tocó y conmutar marcas ahí sería
    // inventar un cambio. Cada toggle deja su propia entrada de deshacer ("preparación: X marcado"),
    // que es el precio de usar el único escritor público de estas claves; a cambio el popup nombra
    // exactamente lo que pasó y deshacer sigue reponiendo el día entero.
    if (dec.changed) for (const k of marcas) toggleSet(viewedDate, k)
    setSelectorOpen(false)
    setAviso(idx == null
      ? 'Hoy descansas.'
      : idx === idxDeHoy
        ? `Vuelves a la rutina de hoy: ${nombreCorto(viewedDate, idx)}.`
        : `Hoy harás ${nombreCorto(viewedDate, idx)}, la del ${planDayWeekday(idx)}.`)
    // El foco no puede quedarse en el aire: al cambiar la rutina se re-dibuja la pantalla entera y el
    // botón pulsado desaparece, lo que dejaría a quien navega con teclado o lector al principio del
    // documento. Si veníamos de descanso el disparador ya no existe (la rama de descanso deja paso a
    // la sesión viva) y el foco va al título de la sesión nueva; si no, vuelve al disparador.
    requestAnimationFrame(() => { (eraDescanso ? h1Ref.current : trigRef.current)?.focus() })
  }

  // Limpieza al cambiar la rutina EFECTIVA del día. Se dispara por el índice, no por el evento del
  // selector, para cubrir también el camino de deshacer (ahí el índice cambia por debajo de la UI:
  // el store repone el día entero y restoreSignal solo limpia lo que se estaba tecleando).
  const lastIdxRef = useRef<{ day: string; idx: number | null }>({ day: viewedDate, idx: pIdx })
  useEffect(() => {
    const prev = lastIdxRef.current
    lastIdxRef.current = { day: viewedDate, idx: pIdx }
    // Cambiar de FECHA no es cambiar de rutina, y este efecto no es la limpieza de ese camino: al
    // navegar, `go` y `pick` resetean exIdx, rirHelp, el calendario, el selector y el aviso. Lo que
    // NO se limpia al cambiar de fecha es `drafts`, `invalid`, `crono`, `dragSet` ni `confirmUndone`:
    // son estado de la sesión de pantalla y sobreviven a propósito (invariante preexistente, hoy
    // inocua porque los días pasados son de solo lectura y no se puede navegar al futuro; el día que
    // exista edición retroactiva habrá que revisarlo).
    if (prev.day !== viewedDate || prev.idx === pIdx) return
    setExIdx(0)            // corrección persistente; el render ya se protege aparte con exIdxSafe
    setCrono(null)         // su clave es de la rutina abandonada: pararlo escribiría los segundos ahí
    setDragSet(null)       // guarda una fila del plan viejo; soltarla reordenaría un índice inexistente
    setConfirmUndone(null) // confirma desmarcar una serie que ya no está en pantalla
    setDrafts({}); setInvalid({}) // lo tecleado pertenece a claves de la rutina abandonada
    setRirHelp(false); setVariantOpen(false); setPrepOpen(false); setPostOpen(false); setSelectorOpen(false)
    // `sugerenciaOff` se deja INTACTO a propósito: si ya descartaste el aviso de ayer, cambiar de
    // rutina no debe resucitarlo.
    if (rest.active && rest.day === viewedDate) discardRest()
    // Deps solo [viewedDate, pIdx]: `rest` cambia cada segundo mientras corre el medidor y meterlo
    // aquí ejecutaría este efecto una vez por tick para nada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedDate, pIdx])

  // ---------- input drafts (numbers persist to the store; drafts keep decimals typeable) ----------
  function fldValue(key: string, field: Field): string {
    const fk = `${key}:${field}`
    if (drafts[fk] !== undefined) return drafts[fk]
    const v = d.log[key]?.[field]
    return v == null ? '' : String(v)
  }
  // Drop a rejection (the value became valid, or the row it described is gone).
  function clearInvalid(...fks: string[]) {
    setInvalid((prev) => {
      if (!fks.some((fk) => prev[fk])) return prev // no-op: never re-render for nothing
      const n = { ...prev }; for (const fk of fks) delete n[fk]; return n
    })
  }
  // Typing channel. THREE invariants, in order of importance:
  //  1. what the user typed is ALWAYS kept in drafts (a rejection never erases the keystrokes),
  //  2. an out-of-limits value is NOT written (no setField -> no store write -> no undo entry),
  //  3. empty writes undefined, never 0 (0 reps / RIR 0 are data, absence is not).
  function onFld(key: string, field: Field, raw: string, kind: CaptureField = field) {
    const fk = `${key}:${field}`
    setDrafts((prev) => ({ ...prev, [fk]: raw }))
    const res = parseCapture(kind, raw)
    if (!res.ok) {
      // Mark now, announce on blur (show:false while typing, see `invalid` above).
      setInvalid((prev) => ({ ...prev, [fk]: { msg: res.message, show: false } }))
      // Fase 4 MUST_FIX: every out-of-range number has an in-range PREFIX that already got
      // persisted while typing (5, 50, 500... then "5000" is rejected). Clear the stored value so
      // the log matches the on-screen "No se guardó": otherwise a phantom 500 kg survives under a
      // red field. The draft keeps the full text; the clear coalesces with the typing undo entry.
      if (d.log[key]?.[field] != null) {
        const patch: { w?: number; r?: number; rir?: number } = {}
        patch[field] = undefined
        setField(viewedDate, key, patch)
      }
      return
    }
    clearInvalid(fk)
    if (res.status === 'pending') return // mid-typing a decimal ("31,"): no error, no write
    const patch: { w?: number; r?: number; rir?: number } = {}
    patch[field] = res.value // 'empty' -> undefined (clears the field), 'ok' -> the number
    setField(viewedDate, key, patch)
  }
  // Reveal the pending rejection message for a field (blur only, see `invalid`).
  function revealInvalid(fk: string) {
    setInvalid((prev) => (prev[fk] && !prev[fk].show ? { ...prev, [fk]: { ...prev[fk], show: true } } : prev))
  }
  // `kind` goes LAST and defaults to the log field, so the only call site that changes is the
  // seconds one (time-based exercises store seconds in the same `r` field).
  function fld(key: string, field: Field, unit: string, ph = '-', onBlur?: (val: string) => void, kind: CaptureField = field) {
    const fk = `${key}:${field}`
    const val = fldValue(key, field)
    const bad = invalid[fk]
    const errId = `err-${key}-${field}`
    return (
      <div className={`fld${val !== '' ? ' filled' : ''}${bad ? ' bad' : ''}`} onClick={(e) => e.stopPropagation()} style={{ flex: '0 0 auto' }}>
        <input inputMode="decimal" value={val} placeholder={ph || '-'}
          aria-invalid={bad ? true : undefined} aria-describedby={errId}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onFld(key, field, e.target.value, kind)}
          onBlur={(e) => { revealInvalid(fk); onBlur?.(e.target.value) }} />
        <span className="u">{unit}</span>
        {/* The node exists ALWAYS (empty while typing) so aria-describedby never changes its target:
            a changing relation makes screen readers re-announce the whole field. polite, not alert. */}
        <p className="err" id={errId} aria-live="polite">{bad?.show ? bad.msg : ''}</p>
      </div>
    )
  }

  // ---------- calendar ----------
  function openCal() {
    const dt = parseDay(viewedDate)
    setCalY(dt.getFullYear()); setCalM(dt.getMonth())
    setCalOpen((o) => !o)
  }
  function shiftMonth(delta: number) {
    let m = calM + delta, y = calY
    if (m < 0) { m = 11; y -= 1 }
    if (m > 11) { m = 0; y += 1 }
    setCalM(m); setCalY(y)
  }
  // Al cambiar de fecha se cierra el selector y se vacía el anuncio: si no, al volver a hoy el
  // selector aparecería abierto solo y el lector de pantalla repetiría un anuncio de otro día.
  function pick(iso: string) {
    if (iso > today) return
    setViewedDate(iso); setExIdx(0); setRirHelp(false); setCalOpen(false); setSelectorOpen(false); setAviso('')
  }

  function go(delta: number) {
    if (delta > 0 && nextDisabled) return
    setViewedDate((v) => addDays(v, delta)); setExIdx(0); setRirHelp(false); setCalOpen(false); setSelectorOpen(false); setAviso('')
  }

  const nav = (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 16 }}>
      <button className="counterBtn" onClick={() => go(-1)} aria-label="Día anterior">‹</button>
      <button onClick={openCal} className="disp" style={{
        fontWeight: 700, fontSize: 16, padding: '6px 14px', borderRadius: 'var(--r-pill)',
        background: 'var(--surface-1)', border: '1px solid var(--line-09)',
        color: isToday ? 'var(--ink-1)' : 'var(--volt)', letterSpacing: '-0.01em',
      }}>{isToday ? 'hoy' : dateLabel(viewedDate)}</button>
      <button className="counterBtn" onClick={() => go(1)} disabled={nextDisabled}
        aria-label="Día siguiente" style={{ opacity: nextDisabled ? 0.35 : 1 }}>›</button>

      {/* Región viva del aviso de cambio de rutina, montada UNA sola vez y aquí dentro a propósito:
          `nav` es el mismo subárbol en las tres ramas de la pantalla (descanso, día pasado y sesión
          viva), así que al recuperar una rutina en domingo React reconcilia este nodo en su sitio y
          el span sobrevive al salto de rama. Con un span por rama, el de descanso se desmontaba y el
          de la sesión viva nacía YA con texto, que es justo lo que NVDA y VoiceOver no anuncian. */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">{aviso}</span>

      {calOpen && <>
        <div onClick={() => setCalOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 39 }} />
        <div className="cal" style={{ top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 8 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <button className="counterBtn sec" onClick={() => shiftMonth(-1)} style={{ width: 24, height: 24, fontSize: 15 }}>‹</button>
            <span className="disp" style={{ fontWeight: 700, fontSize: 13 }}>{monthTitle(calY, calM)}</span>
            <button className="counterBtn sec" onClick={() => shiftMonth(1)} style={{ width: 24, height: 24, fontSize: 15 }}>›</button>
          </div>
          <div className="grid">
            {CAL_HEADERS.map((h) => <div key={h} className="wd">{h}</div>)}
            {monthGrid(calY, calM).map(({ iso, inMonth }) => {
              const future = iso > today
              const cls = `cd${inMonth ? '' : ' off'}${iso === viewedDate ? ' sel' : ''}${iso === today ? ' today' : ''}`
              return (
                <button key={iso} className={cls} disabled={future}
                  onClick={() => pick(iso)}
                  style={{ background: 'none', opacity: future ? 0.3 : 1 }}>
                  {parseDay(iso).getDate()}
                  {dayCounts(state.days[iso]) && <span className="dot" />}
                </button>
              )
            })}
          </div>
        </div>
      </>}
    </div>
  )

  // ---------- día de descanso (hoy: solo el domingo) ----------
  // El texto sigue nombrando al domingo porque es el único día sin rutina del plan; la condición ya
  // no lo nombra, así que un domingo con una rutina recuperada nunca llega hasta aquí.
  if (isRest) {
    return (
      <div>
        {nav}
        <div className="card" style={{ textAlign: 'center', padding: '28px 16px' }}>
          <div style={{ fontSize: 34 }}>😴</div>
          <div className="h1" style={{ marginTop: 8 }}>Domingo: descanso total.</div>
          <p className="tip" style={{ marginTop: 8 }}>El músculo crece fuera del gimnasio. Come, duerme, hidrátate.</p>
        </div>
        {/* Recuperar una rutina en domingo: al elegirla el día deja de ser descanso y el render
            siguiente ya es la sesión viva, sin recargar. Los domingos pasados se quedan como
            descanso: desde aquí no se reescribe la historia. */}
        {isToday && (
          <div style={{ marginTop: 12 }}>
            {/* blockedLabel con el nombre CORTO ("Legs A"): la etiqueta larga mete el día entre
                paréntesis y el mensaje quedaba con dos paréntesis seguidos. En esta rama pIdx es
                null y el nombre corto sería "descanso", pero el mensaje no es alcanzable aquí: para
                bloquear hace falta que el día tenga series, y un día con series nunca es descanso. */}
            <RutinaPicker dayIso={viewedDate} current={null} idxDeHoy={idxDeHoy} moved={false}
              open={selectorOpen} onOpen={() => setSelectorOpen(true)} onClose={() => setSelectorOpen(false)}
              onPick={elegirRutina} blocked={bloqueado} blockedLabel={nombreCorto(viewedDate, pIdx)}
              avisoPrep={soloPrep} statuses={semana} trigRef={trigRef} />
          </div>
        )}
      </div>
    )
  }

  const p = pIdx as number
  const plan = planForDate(viewedDate)[p]
  // Rutina movida: el día de la semana del que viene la rutina que se está pintando, o null si es la
  // que toca por calendario. Sale de isMovedSession (la misma fuente en todas las pantallas), que
  // también cubre el domingo con rutina recuperada.
  const movedFrom = isMovedSession(d) ? planDayWeekday(p) : null

  // ---------- past day: read-only ----------
  if (isPast) {
    return (
      <div>
        {nav}
        {/* El kick del plan numera el día DEL PLAN ("día 2 de 6"), así que en un día con la rutina
            movida se contradiría con la fecha que se está mirando: cuando la rutina viene de otro
            día lo decimos en llano y el kick del plan cede el sitio. */}
        <div className="kick">{movedFrom ? `rutina del ${movedFrom} · movida a este día` : plan.kick}</div>
        <div className="h1" style={{ marginTop: 2 }} data-testid="sesion-plan-name">{plan.name}</div>
        <div className="scrollY" style={{ maxHeight: '68vh', marginTop: 12 }}>
          {plan.ex.map((ex, e) => (
            <div className="group" key={e} style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{e + 1}. {d.variants[`${p}-${e}`] ?? ex.n}</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{ex.meta}</div>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ex.pair
                  ? Array.from({ length: ex.sets }).map((_, r) => {
                    const k0 = pairKey(p, e, r, 0), k1 = pairKey(p, e, r, 1)
                    return (
                      <div key={r} style={{ fontSize: 12 }}>
                        <span className={ex.pair && d.done[k0] && d.done[k1] ? 'note' : 'muted'}>Ronda {r + 1} {d.done[k0] && d.done[k1] ? '✓' : '-'}</span>
                        <PastLog log={d.log[k0]} prefix=" A" />
                        <PastLog log={d.log[k1]} prefix=" · B" />
                      </div>
                    )
                  })
                  : Array.from({ length: normalSetsCount(d, p, e, ex.sets) }).map((_, s) => {
                    const key = setKey(p, e, s)
                    const done = !!d.done[key]
                    const note = d.notes[key]
                    return (
                      <div key={s} style={{ fontSize: 12 }}>
                        <span className={done ? 'note' : 'muted'}>S{s + 1}{s >= ex.sets ? ' (extra)' : ''} {done ? '✓' : '-'}</span>
                        <PastLog log={d.log[key]} prefix=" " />
                        {note && <div className="tip" style={{ marginTop: 2 }}>{note}</div>}
                      </div>
                    )
                  })}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ---------- today: live session ----------
  const { doneN, totN } = sessionProgress(d, p)
  const pct = totN ? Math.round((doneN / totN) * 100) : 0
  const allDone = totN > 0 && doneN === totN
  // Índice de ejercicio SEGURO para este render. El reset a 0 del efecto de limpieza llega tarde:
  // useEffect corre DESPUÉS del render, y el render que ya trae la rutina nueva dereferencia
  // plan.ex[exIdx] con el índice de la abandonada. Las rutinas tienen entre 4 y 7 ejercicios, así que
  // al pasar de una larga (Pull A, 7) a una corta (5) estando en el ejercicio 6, plan.ex[exIdx] sería
  // undefined y la primera lectura (ex.pair) lanzaría en pleno render: sin ErrorBoundary en la app,
  // React desmonta la raíz y se queda la pantalla en blanco en mitad de la sesión. Y como el render
  // lanza, el efecto que iba a arreglarlo nunca llega a ejecutarse. Cubre los tres caminos que mueven
  // la rutina por debajo de la interfaz (selector, chip de ayer y deshacer). El efecto conserva su
  // setExIdx(0) como corrección persistente; esto es el candado del primer render.
  const exIdxSafe = exIdx < plan.ex.length ? exIdx : 0
  const parentEx = plan.ex[exIdxSafe]
  const variantName = d.variants[`${p}-${exIdxSafe}`]
  const activeAlt = variantName ? parentEx.alts?.find((a) => a.n === variantName) : undefined
  // Variant synthesis (audited): the variant is a DIFFERENT implement, so it must NOT inherit the
  // parent's kg seed (t), last mark, technique, tempo or warn - only the structural prescription
  // (sets/reps/rest/meta). A stale variant name whose alt vanished falls back to the parent.
  const ex: Exercise = activeAlt
    ? { ...parentEx, n: activeAlt.n, t: activeAlt.t ?? 'elige un peso dejando 2-3 repeticiones en reserva', last: '', tip: activeAlt.tip, tech: undefined, tempo: undefined, warn: undefined }
    : parentEx
  // How many normal set rows to render (plan sets + any the user added). Extras are a bonus: they do
  // NOT feed sessionProgress/exComplete above, only the render + the all-sets-done auto-advance gate.
  const setsCount = ex.pair ? ex.sets : normalSetsCount(d, p, exIdxSafe, ex.sets)
  // Smart resolver: override -> own history -> related (logged | plan-seed) -> own seed -> profile
  // estimate -> machine reference -> first time. Resolves for the ACTIVE (variant) name, so a swapped
  // exercise gets its own history/estimate. todayIso feeds age-from-dob.
  const res = resolveTarget({ history, ex, beforeDate: viewedDate, override: d.weights[weightKey(p, exIdxSafe)], profile, currentBw, todayIso: today })
  const target = res.target
  const tph = res.ph
  // Every estimate kind (dashed) is a NEW/uncertain movement -> 2-3 RIR; a known/committed lift keeps 1-2.
  const isEstimate = res.kind === 'suggest' || res.kind === 'suggest-seed' || res.kind === 'suggest-profile' || res.kind === 'suggest-reference'
  const rirPh = isEstimate || res.kind === 'first' ? '2-3' : '1-2'
  // Variant switch is blocked once today's sets for this slot carry data (identity integrity: sets
  // logged under one implement must not re-attribute). {at}/{rest} residue never blocks.
  const slotHasData = (() => {
    const prefix = `${p}-${exIdxSafe}-`
    const hasData = (k: string) => !!d.done[k] || d.log[k]?.w != null || d.log[k]?.r != null || d.log[k]?.rir != null
    return [...Object.keys(d.done), ...Object.keys(d.log)].some((k) => k.startsWith(prefix) && k.split('-').length === 3 && hasData(k))
  })()
  // TIME-based exercise (farmer carry, planks): the target prescribes seconds -> the set logs
  // seconds (stored in the same r field: persistence/reorder/notes work unchanged) + a stopwatch.
  // SF25: este regex es SOLO un DETECTOR (decide el canal de la fila: repeticiones o segundos).
  // Los LIMITES de ese canal viven exclusivamente en capture.ts (CAPTURE_LIMITS.sec) y todos los
  // escritores pasan por ahi: el tecleo via parseCapture('sec', ...) y el cronometro via
  // clampStopwatchSeconds. Aqui nunca se validan rangos.
  const isTimeEx = /\bsegundos?\b|\d+\s*s\b/.test(ex.t)
  const segPh = ex.t.match(/(\d+(?:-\d+)?)\s*(?:segundos?|s\b)/)?.[1] ?? ''
  const band = restBand(ex)
  const restChip = (key: string) => {
    const r = d.log[key]?.rest
    if (r == null) return null
    const z = zoneOf(r, band)
    const color = z === 'ideal' ? 'var(--volt)' : z === 'over' ? 'var(--warn)' : 'var(--ink-3)'
    return <span className="disp" style={{ fontSize: 10, fontWeight: 700, color, whiteSpace: 'nowrap' }} title="descanso registrado">{mmss(r)}</span>
  }
  const tipText = ex.tech || ex.tip

  function scheduleAdvance() {
    setTimeout(() => { setExIdx((i) => Math.min(i + 1, plan.ex.length - 1)); setRirHelp(false) }, 350)
  }
  function toggleNormal(e: number, exercise: Exercise, s: number) {
    const key = setKey(p, e, s)
    const wasDone = !!d.done[key]
    if (wasDone) {
      // Confirm before un-completing (accidental tap protection). The sheet names the exact set.
      setConfirmUndone({ key, body: `La serie S${s + 1} de ${exercise.n} volverá a quedar pendiente.` })
      return
    }
    toggleSet(viewedDate, key)
    setField(viewedDate, key, { at: Date.now() }) // completion timestamp → time dimension
    startRest(restBand(exercise), viewedDate, key) // count-up rest meter for this set
    const nextDone = { ...d.done, [key]: 1 as const }
    // Auto-advance only when EVERY rendered set (plan + extras) is done, so an added bonus set holds
    // the advance until you actually do it (completion/% bar stay plan-based via sessionProgress).
    if (allNormalSetsDone({ ...d, done: nextDone }, p, e, exercise.sets)) scheduleAdvance()
  }
  // Drag a set row by its handle to reorder (payload permutation; see rules.reorderSetData). Pointer
  // events (works on touch; touch-action none on the handle stops the page scroll). Row pitch ~60px
  // (row 52 + gap 8); the drop index derives from the vertical delta, clamped to the rendered range.
  function onSetDragStart(s: number, ev: React.PointerEvent) {
    ev.stopPropagation(); ev.preventDefault()
    const startY = ev.clientY
    const count = setsCount
    const move = (e2: PointerEvent) => setDragSet({ from: s, dy: e2.clientY - startY })
    const up = (e2: PointerEvent) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up)
      setDragSet(null)
      const to = Math.max(0, Math.min(count - 1, s + Math.round((e2.clientY - startY) / 60)))
      if (to !== s) {
        reorderSet(viewedDate, p, exIdxSafe, s, to, ex.sets)
        // Clear this slot's input drafts (and their rejections): values now live at different indices.
        setDrafts((prev) => { const n = { ...prev }; for (const k of Object.keys(n)) if (k.startsWith(`${p}-${exIdxSafe}-`)) delete n[k]; return n })
        setInvalid((prev) => { const n = { ...prev }; for (const k of Object.keys(n)) if (k.startsWith(`${p}-${exIdxSafe}-`)) delete n[k]; return n })
      }
    }
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up)
    setDragSet({ from: s, dy: 0 })
  }

  // On leaving a NORMAL weight input, copy that weight to the BELOW sets that are still empty + not done
  // (cascadeWeightKeys is non-destructive: empty-only, weight-only, never a done or typed set). Runs on
  // blur (not per keystroke) so intermediate digits never leak into other rows. Value read from the DOM.
  function onWeightBlur(s: number, rawVal: string) {
    // SAME limits as the typing channel: without this a rejected 900 would still reach the sets
    // below through the back door (this path used to accept any n > 0, with no ceiling).
    const res = parseCapture('w', rawVal)
    if (!res.ok || res.status !== 'ok') return // empty / mid-typing / out of limits: never cascade
    const keys = cascadeWeightKeys(getDay(viewedDate), p, exIdxSafe, s, ex.sets)
    if (keys.length === 0) return
    cascadeWeight(viewedDate, keys, res.value)
    setDrafts((prev) => { const nn = { ...prev }; for (const k of keys) delete nn[`${k}:w`]; return nn })
    clearInvalid(...keys.map((k) => `${k}:w`)) // the destinations now hold a valid weight
  }
  function toggleChild(e: number, exercise: Exercise, round: number, child: number) {
    const key = pairKey(p, e, round, child)
    const wasDone = !!d.done[key]
    if (wasDone) {
      setConfirmUndone({ key, body: `La ronda ${round + 1} (${child === 0 ? 'A' : 'B'}) de ${exercise.n} volverá a quedar pendiente.` })
      return
    }
    toggleSet(viewedDate, key)
    setField(viewedDate, key, { at: Date.now() })
    const nextDone = { ...d.done, [key]: 1 as const }
    const other = pairKey(p, e, round, child === 0 ? 1 : 0)
    if (nextDone[key] && nextDone[other]) {
      startRest(restBand(exercise), viewedDate, key)
      if (exComplete({ ...d, done: nextDone }, p, e, exercise)) scheduleAdvance()
    }
  }

  return (
    <div>
      {nav}

      {/* header */}
      {/* Mismo motivo que en el día pasado: el kick numera el día del plan y en una rutina movida
          mentiría sobre la fecha. */}
      <div className="kick">{movedFrom ? `rutina del ${movedFrom} · la haces hoy` : plan.kick}</div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginTop: 2 }}>
        {/* tabIndex -1: el título recibe el foco cuando el disparador del selector desaparece
            (domingo que deja de ser descanso). No es alcanzable con Tab. */}
        <div className="h1" ref={h1Ref} tabIndex={-1} data-testid="sesion-plan-name">{plan.name}</div>
        <div className="disp" data-testid="sesion-progress" style={{ fontWeight: 700, fontSize: 14, color: 'var(--volt)' }}>{doneN}/{totN}</div>
      </div>
      <div className="bar" style={{ marginTop: 10 }}><i style={{ width: `${pct}%` }} /></div>

      {/* Selector de rutina: línea propia bajo la barra de progreso, nunca dentro de la fila del
          título (ahí comprimiría el contador y se envolvería en pantallas de 390 px). */}
      <div style={{ marginTop: 10 }}>
        <RutinaPicker dayIso={viewedDate} current={pIdx} idxDeHoy={idxDeHoy} moved={movedFrom != null}
          open={selectorOpen} onOpen={() => setSelectorOpen(true)} onClose={() => setSelectorOpen(false)}
          onPick={elegirRutina} blocked={bloqueado} blockedLabel={nombreCorto(viewedDate, pIdx)}
          avisoPrep={soloPrep} statuses={semana} trigRef={trigRef} />
      </div>

      {/* Aviso de descubrimiento: ayer era día de rutina y no entrenaste, hoy todavía está en blanco.
          No se menciona la racha a propósito: la racha ya perdona un fallo, así que amenazar con
          perderla sería mentir. Descartarlo no escribe nada. */}
      {recuperable && !sugerenciaOff && (
        <div className="chipSug" data-testid="sesion-chip-ayer" style={{ marginTop: 12 }}>
          {/* Dos ideas distintas que antes se mezclaban en una: el DÍA DE CALENDARIO que fue ayer sale
              de la FECHA (DOWS[dow(recuperable.day)]), y el día al que pertenece la RUTINA sale del
              índice (planDayWeekday). Antes el "Ayer <día>" se derivaba del índice, así que si ayer
              tenía a su vez una rutina movida (justo lo que este milestone habilita) el chip
              afirmaba un hecho de calendario falso: "Ayer lunes" en un miércoles. Cuando los dos
              coinciden (el caso normal) no se repite el día: se dice "esa rutina" y ya. */}
          <p className="tip" style={{ margin: 0 }}>
            Ayer {DOWS[dow(recuperable.day)]} no entrenaste. {planDayWeekday(recuperable.idx) === DOWS[dow(recuperable.day)]
              ? `¿Haces hoy esa rutina (${nombreCorto(recuperable.day, recuperable.idx)})?`
              : `¿Haces hoy la rutina del ${planDayWeekday(recuperable.idx)} (${nombreCorto(recuperable.day, recuperable.idx)})?`}
          </p>
          <div className="acts">
            <button className="opt sel" data-testid="sesion-chip-ayer-si"
              onClick={() => elegirRutina(recuperable.idx)}>Sí, la hago hoy</button>
            {/* Al descartar, el chip entero se desmonta con el foco dentro y el foco caería al body:
                se devuelve al disparador del selector (mismo mecanismo que elegirRutina) y se
                confirma por la región viva, que si no el descarte es mudo para un lector. */}
            <button className="opt" data-testid="sesion-chip-ayer-no"
              onClick={() => {
                setSugerenciaOff(true)
                setAviso(`Sigues con ${nombreCorto(viewedDate, pIdx)}.`)
                requestAnimationFrame(() => trigRef.current?.focus())
              }}>No, sigo con {nombreCorto(viewedDate, pIdx)}</button>
          </div>
        </div>
      )}

      {allDone && <div className="note" style={{ marginTop: 12, textAlign: 'center' }}>Sesión cerrada. Así se construye. 🔥</div>}

      {/* preparación · antes de empezar */}
      <div style={{ marginTop: 12 }}>
        {/* El día que se escribe es el que se está VIENDO, no `today`. Hoy es inocuo porque esta rama
            solo se renderiza cuando viewedDate === today (isPast retorna antes), pero era la única
            escritura del archivo que no usaba viewedDate: en cuanto exista edición retroactiva, marcar
            un paso de preparación mientras miras otro día lo escribiría en el día equivocado.
            La lista de items ya se lee con prepForDate(viewedDate), así que el par lectura/escritura
            queda por fin en la misma fecha. */}
        <Checklist title="Preparación" hint="antes de empezar" items={prepForDate(viewedDate)[p]} prefix="prep"
          done={d.done} onToggle={(k) => toggleSet(viewedDate, k)} open={prepOpen} setOpen={setPrepOpen}
          footer="El estiramiento estático va al cierre, no ahora." />
      </div>

      {/* toda la sesión */}
      <div className="kick" style={{ marginTop: 16, marginBottom: 6 }}>toda la sesión</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {plan.ex.map((x, e) => {
          const c = exComplete(d, p, e, x)
          const cur = e === exIdxSafe
          return (
            <button key={e} onClick={() => { setExIdx(e); setRirHelp(false); setVariantOpen(false) }} style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
              padding: '8px 10px', borderRadius: 'var(--r-sm)',
              background: cur ? 'var(--volt-fill-07)' : 'transparent',
              border: '1px solid', borderColor: cur ? 'var(--volt-border-30)' : 'var(--line-06)',
            }}>
              <span className="sn" style={{ color: cur ? 'var(--volt)' : 'var(--ink-3)' }}>{e + 1}</span>
              <span style={{ flex: 1, fontSize: 12.5, fontWeight: cur ? 600 : 500, color: c ? 'var(--ink-3)' : 'var(--ink-1)' }}>{d.variants[`${p}-${e}`] ?? x.n}</span>
              <div className={`ck${c ? ' on' : ''}`} style={{ width: 18, height: 18, fontSize: 11, borderWidth: 2 }}>{c ? '✓' : ''}</div>
            </button>
          )
        })}
      </div>

      {/* focused block */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div className="row" style={{ gap: 7, alignItems: 'center' }}>
              <span className="sn" style={{ color: 'var(--ink-3)' }}>{exIdxSafe + 1}</span>
              <span className="disp" style={{ fontWeight: 700, fontSize: 16 }}>{ex.n}</span>
              {ex.tempo && <span className="tempo">{ex.tempo}</span>}
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 3 }}>{ex.meta}</div>
          </div>
        </div>

        {ex.warn && <div className="warn" style={{ marginTop: 10 }}>{ex.warn}</div>}

        <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {isEstimate ? (
            // An estimate (related lift, plan seed, or profile), NOT a logged fact. Dashed outline +
            // "~" so it can never be mistaken for the solid committed "hoy:" chip. The aria-label
            // includes the weight so a screen reader announces the number, not just "sugerido".
            <span className="sugbadge" aria-label={`${res.kind === 'suggest-reference' ? 'referencia' : res.kind === 'suggest-profile' ? 'peso inicial sugerido' : 'peso sugerido'} ${res.target}`}>
              {res.kind === 'suggest-reference' ? 'referencia' : res.kind === 'suggest-profile' ? 'sugerido inicial' : 'sugerido'} {res.target}
            </span>
          ) : res.kind === 'first' ? null : (
            <>
              {(res.kind === 'history' ? res.lastStr : ex.last) && (
                <span className="daybadge">últ: {res.kind === 'history' ? res.lastStr : ex.last}</span>
              )}
              <span className="chip">hoy: {target}</span>
            </>
          )}
          <button className="link" onClick={() => {
            const v = window.prompt(`Peso de hoy · ${ex.n}`, target)
            if (v && v.trim()) setTargetOverride(viewedDate, weightKey(p, exIdxSafe), v.trim())
          }} style={{ fontSize: 12, padding: 0, marginLeft: 'auto' }}>✎ peso</button>
        </div>

        {isEstimate && (
          <div style={{ marginTop: 6 }}>
            {/* The BASIS line, honest per tier: a LOGGED lift (marca real) vs a plan number never lifted
                vs the user's bodyweight. Never call a plan seed a "marca" (that means an achieved PR). */}
            {res.kind === 'suggest' && (
              <p className="tip" style={{ margin: 0 }}>basado en tu {res.basisLift} de {res.basisWeight} kg</p>
            )}
            {res.kind === 'suggest-seed' && (
              <p className="tip" style={{ margin: 0 }}>estimado a partir del peso inicial de tu plan para {res.basisLift} ({res.basisWeight} kg)</p>
            )}
            {res.kind === 'suggest-profile' && (
              <p className="tip" style={{ margin: 0 }}>estimado inicial según tu peso ({Math.round(res.bodyWeight ?? 0)} kg)</p>
            )}
            {res.kind === 'suggest-reference' && (
              <p className="tip" style={{ margin: 0 }}>
                {target.startsWith('asistencia')
                  ? 'más asistencia = más fácil: progresas BAJANDO la asistencia poco a poco'
                  : 'las máquinas varían por marca: es una referencia según tu peso, no un objetivo'}
              </p>
            )}
            <p className="tip" style={{ margin: '2px 0 0', color: 'var(--ink-3)' }}>es un punto de partida, no un máximo: empieza por debajo y deja 2-3 reps en reserva</p>
            {res.kind === 'suggest-profile' && (ex.n === 'Peso muerto rumano' || ex.n === 'Hip thrust') && (
              <p className="tip" style={{ margin: '2px 0 0', color: 'var(--ink-3)' }}>prioriza la técnica antes que el peso; si es tu primera vez con este movimiento, empieza más liviano</p>
            )}
            <p className="tip" style={{ margin: '2px 0 0', color: 'var(--ink-3)' }}>si dudas baja el peso; tu primer set real fija tu marca y la próxima vez ya no es estimado</p>
          </div>
        )}
        {res.kind === 'first' && <p className="tip" style={{ marginTop: 10 }}>{res.cue}</p>}

        {tipText && <p className="tip" style={{ marginTop: 10 }}>{tipText}</p>}

        {/* Variant picker: same movement, another implement (bundle alts). Hidden for supersets and
            for plans without alts (static path unchanged). Switch blocked once today's sets carry
            data (identity integrity); the blocked reason renders as visible inline text. */}
        {!parentEx.pair && (parentEx.alts?.length ?? 0) > 0 && (
          <div style={{ marginTop: 10 }}>
            <button className="link" style={{ fontSize: 12, padding: 0 }}
              aria-expanded={variantOpen} aria-controls="variant-picker"
              onClick={() => setVariantOpen((v) => !v)}>
              cambiar ejercicio {variantOpen ? '▴' : '▾'}
            </button>
            {variantOpen && slotHasData && (
              <p className="tip" style={{ marginTop: 6, color: 'var(--warn)' }} role="alert">
                Hoy ya registraste series de {ex.n}; podrás cambiarlo mañana (así tu progreso queda bien contado).
              </p>
            )}
            {variantOpen && !slotHasData && (
              <div id="variant-picker" role="radiogroup" aria-label="variantes del ejercicio" style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[{ n: parentEx.n, tip: undefined as string | undefined, original: true }, ...(parentEx.alts ?? []).map((a) => ({ n: a.n, tip: a.tip, original: false }))].map((opt) => {
                  const on = (variantName ?? parentEx.n) === opt.n
                  return (
                    <button key={opt.n} role="radio" aria-checked={on}
                      className={on ? 'opt sel' : 'opt'}
                      style={{ width: '100%', minHeight: 44, textAlign: 'left', whiteSpace: 'normal' }}
                      onClick={() => { setVariant(viewedDate, p, exIdxSafe, opt.original ? null : opt.n); setVariantOpen(false) }}>
                      {opt.n}{opt.original ? ' (original)' : ''}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* set rows */}
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ex.pair
            ? Array.from({ length: ex.sets }).map((_, r) => {
              const k0 = pairKey(p, exIdxSafe, r, 0), k1 = pairKey(p, exIdxSafe, r, 1)
              const roundDone = !!d.done[k0] && !!d.done[k1]
              return (
                <div key={r} className={`ssr${roundDone ? ' done' : ''}`}>
                  <div className="kick" style={{ fontSize: 10 }}>Ronda {r + 1}</div>
                  {[0, 1].map((child) => {
                    const key = child === 0 ? k0 : k1
                    const info = ex.pair![child]
                    const cph = targetPlaceholders(info.t)
                    const done = !!d.done[key]
                    return (
                      <div key={child} className={`set${done ? ' done' : ''}`} style={{ cursor: 'pointer' }}
                        onClick={() => toggleChild(exIdxSafe, ex, r, child)}>
                        <span className="sn">{child === 0 ? 'A' : 'B'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.n}</div>
                          <div className="tg" style={{ fontSize: 11 }}>{info.t}</div>
                        </div>
                        {fld(key, 'w', 'kg', cph.w)}
                        {/* Mismo detector de tiempo que el ejercicio simple pero sobre el objetivo del
                            HIJO: un hijo de superserie por segundos valida 1..3600, no 1..50 reps
                            (Fase 4 ronda 2, cierre de SF25; hoy ningun pair del plan prescribe
                            segundos, este es el candado para cuando exista). */}
                        {/\bsegundos?\b|\d+\s*s\b/.test(info.t ?? '')
                          ? fld(key, 'r', 'seg', cph.r, undefined, 'sec')
                          : fld(key, 'r', 'reps', cph.r)}
                        {restChip(key)}
                        <div className={`ck${done ? ' on' : ''}`}>{done ? '✓' : ''}</div>
                      </div>
                    )
                  })}
                </div>
              )
            })
            : Array.from({ length: setsCount }).map((_, s) => {
              const key = setKey(p, exIdxSafe, s)
              const done = !!d.done[key]
              const isRirSet = s === ex.sets - 1        // RIR stays on the last PLAN set (visible even after an extra is added)
              const isExtra = s >= ex.sets              // added beyond the plan's prescribed sets
              const note = d.notes[key]
              const dragging = dragSet?.from === s
              return (
                <div key={s} style={dragging ? { transform: `translateY(${dragSet!.dy}px)`, zIndex: 5, position: 'relative', opacity: 0.85 } : undefined}>
                  <div className={`set${done ? ' done' : ''}`} style={{ cursor: 'pointer' }}
                    onClick={() => toggleNormal(exIdxSafe, ex, s)}>
                    {/* Drag handle: reorder this set (e.g. an added set that was really done first). */}
                    <span onPointerDown={(ev) => onSetDragStart(s, ev)} aria-label={`arrastra para reordenar la serie ${s + 1}`}
                      style={{ cursor: 'grab', touchAction: 'none', color: 'var(--ink-3)', fontSize: 13, padding: '0 2px', userSelect: 'none' }}>⠿</span>
                    <span className="sn">S{s + 1}</span>
                    {isExtra && <span className="disp" style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--volt)', letterSpacing: 0.3 }}>extra</span>}
                    {fld(key, 'w', 'kg', tph.w, (v) => onWeightBlur(s, v))}
                    {isTimeEx ? (
                      <>
                        {/* 'sec': this r field holds SECONDS here, so it validates 1..3600, not 1..50 reps. */}
                        {fld(key, 'r', 'seg', segPh, undefined, 'sec')}
                        {/* Stopwatch: tap to start counting, tap again to stop and write the seconds. */}
                        <button className="link" aria-label={crono?.key === key ? 'detener cronómetro y guardar segundos' : 'iniciar cronómetro de la serie'}
                          onClick={(ev) => {
                            ev.stopPropagation()
                            if (crono?.key === key) {
                              // SF4/SF17: clamp con la fuente unica (CAPTURE_LIMITS.sec en
                              // capture.ts). Una medicion de mas de 1 hora no es una serie real
                              // (es un cronometro olvidado), asi que se recorta a 3600 en vez de
                              // escribir un valor fuera de rango que el canal de tecleo rechazaria.
                              const secs = clampStopwatchSeconds(Date.now() - crono.start)
                              setField(viewedDate, key, { r: secs })
                              setDrafts((prev) => { const n = { ...prev }; delete n[`${key}:r`]; return n })
                              clearInvalid(`${key}:r`) // clampado arriba: la escritura siempre queda dentro de CAPTURE_LIMITS.sec
                              setCrono(null)
                            } else {
                              setCrono({ key, start: Date.now(), now: Date.now() })
                            }
                          }}
                          style={{ fontSize: 12, padding: '0 2px', color: crono?.key === key ? 'var(--volt)' : undefined, fontVariantNumeric: 'tabular-nums' }}>
                          {crono?.key === key ? mmss(Math.round((crono.now - crono.start) / 1000)) : '⏱'}
                        </button>
                      </>
                    ) : (
                      <>
                        {fld(key, 'r', 'reps', tph.r)}
                        {isRirSet && fld(key, 'rir', 'RIR', rirPh)}
                      </>
                    )}
                    <span style={{ flex: 1 }} />
                    {restChip(key)}
                    {/* Per-set note. stopPropagation: interactive elements inside the toggle row must
                        never bubble into a set toggle (audited invariant). */}
                    <button className="link" aria-label={`nota de la serie ${s + 1}`}
                      onClick={(ev) => {
                        ev.stopPropagation()
                        const v = window.prompt(`Nota · S${s + 1} · ${ex.n}`, note ?? '')
                        if (v !== null) setNote(viewedDate, key, v.trim())
                      }}
                      style={{ fontSize: 12, padding: '0 2px', opacity: note ? 1 : 0.55 }}>📝</button>
                    <div className={`ck${done ? ' on' : ''}`}>{done ? '✓' : ''}</div>
                  </div>
                  {note && <p className="tip" style={{ margin: '3px 0 0 30px', fontSize: 11.5 }}>{note}</p>}
                </div>
              )
            })}
        </div>

        {/* add / remove extra sets (normal exercises only; V2 has no supersets) - OUTSIDE the toggle
            rows so the tap never bubbles to a set toggle. "quitar" removes the last extra when not done. */}
        {!ex.pair && (
          <div className="row" style={{ gap: 14, marginTop: 8, alignItems: 'center' }}>
            <button className="link" style={{ fontSize: 12, padding: 0 }} onClick={() => {
              const lastW = d.log[setKey(p, exIdxSafe, setsCount - 1)]?.w // clone the last set's weight
              addSet(viewedDate, setKey(p, exIdxSafe, setsCount), lastW)
            }}>+ agregar serie</button>
            {setsCount > ex.sets && !d.done[setKey(p, exIdxSafe, setsCount - 1)] && (
              <button className="link" style={{ fontSize: 12, padding: 0, color: 'var(--ink-3)' }} onClick={() => {
                const key = setKey(p, exIdxSafe, setsCount - 1)
                removeSet(viewedDate, key)
                setDrafts((prev) => { const n = { ...prev }; for (const f of ['w', 'r', 'rir']) delete n[`${key}:${f}`]; return n })
                clearInvalid(...['w', 'r', 'rir'].map((f) => `${key}:${f}`)) // the row is gone
              }}>quitar última</button>
            )}
          </div>
        )}

        {/* RIR helper - normal exercises only (RIR sits on the last set) */}
        {!ex.pair && (
          <div style={{ marginTop: 8 }}>
            <button className="link" onClick={() => setRirHelp((v) => !v)} style={{ fontSize: 11.5, padding: 0 }}>
              ¿cómo lo mido? →
            </button>
            {rirHelp && (
              <p className="tip" style={{ marginTop: 6 }}>
                RIR = reps en reserva al terminar la serie. <b>0</b> = fallo total, <b>1</b> = te quedaba una rep, <b>2</b> = te quedaban dos. En la última serie apunta a 0-1.
              </p>
            )}
          </div>
        )}
      </div>

      {/* cierre · después de entrenar */}
      <div style={{ marginTop: 10 }}>
        {/* Mismo motivo que en Preparación: se escribe sobre viewedDate (la fecha que se está
            mirando), no sobre `today`. Los items ya vienen de cooldownForDate(viewedDate); dejar la
            escritura en `today` era una discrepancia latente, no un fallo visible hoy. */}
        <Checklist title="Cierre" hint="después de entrenar" items={cooldownForDate(viewedDate)[p]} prefix="post"
          done={d.done} onToggle={(k) => toggleSet(viewedDate, k)} open={postOpen} setOpen={setPostOpen} />
      </div>

      {confirmUndone && (
        <ConfirmSheet title="desmarcar serie" body={confirmUndone.body} actionLabel="Desmarcar"
          onCancel={() => setConfirmUndone(null)}
          onConfirm={() => { toggleSet(viewedDate, confirmUndone.key); setConfirmUndone(null) }} />
      )}
    </div>
  )
}

/**
 * Selector de la rutina del día (sesión movible).
 *
 * Clonado del patrón del selector de variantes de ejercicio (el único patrón accesible ya auditado
 * de esta pantalla): disparador con aria-expanded/aria-controls, lista con role="radiogroup" y
 * opciones role="radio" + aria-checked, objetivo táctil de 44 px y texto que se envuelve en vez de
 * recortarse (390 px). Escape cierra y devuelve el foco al disparador.
 *
 * TECLADO (Fase 4): un role="radio" se anuncia como "botón de opción, 1 de 6", o sea PROMETE flechas.
 * Aquí las flechas mueven el FOCO entre opciones (arriba/izquierda, abajo/derecha, con vuelta, más
 * Inicio/Fin) y solo con Tab entra una opción, la marcada (tabindex móvil). Desviación deliberada del
 * patrón ARIA estricto, donde mover el foco también marcaría: aquí marcar ESCRIBE (cambia la rutina
 * del día, con su entrada de deshacer y su escritura a la base), así que recorrer la lista con las
 * flechas no puede ir dejando cambios detrás. Para elegir hay que confirmar con Intro o Espacio, que
 * es lo que ya hace un <button> nativo.
 *
 * Es de PRESENTACIÓN PURA (sin useStore ni estado propio) porque se monta en dos ramas distintas de
 * la pantalla, la de descanso y la de sesión viva, y las dos deben comportarse igual.
 *
 * El rótulo de cada opción sale del ÍNDICE (planDayLabel, que traduce índice -> día), nunca del
 * texto del plan: si un día se regenera el plan con otro orden, la etiqueta seguiría siendo cierta.
 */
function RutinaPicker({ dayIso, current, idxDeHoy, moved, open, onOpen, onClose, onPick, blocked, blockedLabel, avisoPrep, statuses, trigRef }: {
  dayIso: string                       // fecha que se está viendo (resuelve el plan de esa fecha)
  current: number | null               // rutina efectiva de hoy (null = descanso)
  idxDeHoy: number | null              // la que toca por calendario: destino de "volver a..."
  moved: boolean                       // la rutina de hoy vino de otro día
  open: boolean
  onOpen: () => void
  onClose: () => void
  onPick: (idx: number | null) => void
  blocked: boolean                     // el día ya tiene series: cambiar mezclaría dos rutinas
  blockedLabel: string                 // rutina actual, para explicar el bloqueo sin nombres cableados
  avisoPrep: boolean                   // hay pasos de preparación/cierre marcados (no bloquea)
  statuses: PlanDayWeekStatus[]        // estado de las 6 rutinas de la semana en curso
  // MutableRefObject (no RefObject): `current` tiene que ser escribible, que es lo que devuelve
  // useRef<HTMLButtonElement | null>(null) en la pantalla y lo que React necesita para asignarlo.
  trigRef: React.MutableRefObject<HTMLButtonElement | null>
}) {
  // Botones de las opciones, para mover el foco con las flechas. Es un array de refs, no estado: el
  // foco no pinta nada, así que cambiarlo no debe re-dibujar.
  const optRefs = useRef<(HTMLButtonElement | null)[]>([])
  // Única opción tabulable (tabindex móvil): la marcada, y la primera cuando no hay ninguna (día de
  // descanso). Así el grupo entero es UNA parada de Tab y dentro se navega con flechas, que es lo
  // que un role="radiogroup" promete a un lector de pantalla.
  const idxTabulable = current != null && current >= 0 && current < statuses.length ? current : 0
  // Flechas, Inicio y Fin sobre el grupo: mueven el foco con vuelta (del último al primero). El
  // índice de partida sale del elemento enfocado, no de un estado, para que el manejador viva en el
  // div del grupo (un solo sitio) y no se duplique en las seis opciones.
  function onFlechas(e: React.KeyboardEvent<HTMLDivElement>) {
    const n = statuses.length
    if (n === 0) return
    const desde = optRefs.current.indexOf(e.target as HTMLButtonElement)
    if (desde === -1) return // el foco no está en una opción: no es asunto nuestro
    const destino = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? (desde + 1) % n
      : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? (desde - 1 + n) % n
        : e.key === 'Home' ? 0
          : e.key === 'End' ? n - 1
            : -1
    if (destino === -1) return
    e.preventDefault()  // las flechas arriba/abajo no deben además desplazar la página
    e.stopPropagation()
    optRefs.current[destino]?.focus()
  }
  return (
    <div onKeyDown={(e) => {
      if (e.key !== 'Escape' || !open) return
      e.stopPropagation()
      onClose()
      trigRef.current?.focus()
    }}>
      <button ref={trigRef} className="rutinaTrig" data-testid="sesion-selector"
        aria-expanded={open} aria-controls="rutina-picker"
        onClick={() => (open ? onClose() : onOpen())}>
        <span style={{ flex: 1 }}>{current == null ? 'hoy descansas · ¿recuperas una rutina?' : 'cambiar rutina'}</span>
        <span className="muted" aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>

      {/* Salida visible sin abrir el selector: si la rutina de hoy vino de otro día, se puede volver
          a la del calendario de un toque. Con series ya registradas no se ofrece (estaría bloqueada
          igual que el resto de cambios). */}
      {moved && !blocked && (
        <button className="link" data-testid="sesion-selector-revert"
          style={{ display: 'block', width: '100%', minHeight: 44, marginTop: 4, textAlign: 'left', fontSize: 12.5, whiteSpace: 'normal' }}
          onClick={() => onPick(idxDeHoy)}>
          {idxDeHoy == null ? 'volver al descanso de hoy' : `volver a la rutina del ${planDayWeekday(idxDeHoy)}`}
        </button>
      )}

      {open && blocked && (
        <p className="warn" role="alert" data-testid="sesion-selector-bloqueo" style={{ marginTop: 8 }}>
          Hoy ya registraste series de {blockedLabel}; podrás cambiar de rutina mañana (así tu progreso queda bien contado).
        </p>
      )}

      {open && !blocked && (
        <>
          {/* Copy veraz: cada rutina tiene su propia lista de preparación y cierre, así que al
              cambiar solo se conservan los pasos que también están en la lista de la rutina nueva
              (se emparejan por nombre) y el resto se desmarcan. Antes decía que se quedaban todos
              marcados "porque son del día", y eso dejaba marcado un paso que nadie hizo, incluido el
              calentamiento de hombro con ⚠. */}
          {avisoPrep && (
            <p className="tip" data-testid="sesion-selector-aviso-prep" style={{ marginTop: 8 }}>
              De preparación y cierre se mantienen los pasos que también están en la rutina nueva; los demás se desmarcan.
            </p>
          )}
          <div id="rutina-picker" role="radiogroup" aria-label="rutina de hoy" onKeyDown={onFlechas}
            style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {statuses.map((st, i) => {
              const on = current === i
              // "hoy" = la que toca por calendario; "hecha"/"pendiente" miran el día de calendario de
              // esa rutina en la semana en curso. "porVenir" no lleva coletilla: no hay nada que decir.
              const sub = st === 'hoy' ? '(la que toca hoy)'
                : st === 'hecha' ? '(ya la hiciste)'
                  : st === 'pendiente' ? '(no la hiciste)' : ''
              return (
                <button key={i} role="radio" aria-checked={on} data-testid={`sesion-selector-opt-${i}`}
                  ref={(el) => { optRefs.current[i] = el }}
                  tabIndex={i === idxTabulable ? 0 : -1}
                  className={on ? 'opt stack sel' : 'opt stack'} style={{ width: '100%', minHeight: 44 }}
                  onClick={() => onPick(i)}>
                  <span>{planDayLabel(dayIso, i)}</span>
                  {sub && <span className="optSub">{sub}</span>}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function Checklist({ title, hint, items, prefix, done, onToggle, open, setOpen, footer }: {
  title: string; hint: string; items: PrepItem[]; prefix: string
  done: Record<string, 1>; onToggle: (key: string) => void
  open: boolean; setOpen: (v: boolean) => void; footer?: string
}) {
  const count = items.filter((_, i) => done[`${prefix}-${i}`]).length
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <button onClick={() => setOpen(!open)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: 'none', textAlign: 'left' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
          <div className="muted" style={{ fontSize: 11, marginTop: 1 }}>{hint} · {count}/{items.length}</div>
        </div>
        <span className="muted" style={{ fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((it, i) => {
            const isDone = !!done[`${prefix}-${i}`]
            return (
              <div key={i} className={`set${isDone ? ' done' : ''}`} onClick={() => onToggle(`${prefix}-${i}`)} style={{ cursor: 'pointer', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: it.warn && !isDone ? 'var(--warn)' : undefined }}>{it.warn ? '⚠ ' : ''}{it.label}</div>
                  {it.detail && <div className="tip" style={{ fontSize: 11, marginTop: 2 }}>{it.detail}</div>}
                </div>
                <div className={`ck${isDone ? ' on' : ''}`} style={{ width: 22, height: 22, fontSize: 13 }}>{isDone ? '✓' : ''}</div>
              </div>
            )
          })}
          {footer && <div className="muted" style={{ fontSize: 10.5, marginTop: 2 }}>{footer}</div>}
        </div>
      )}
    </div>
  )
}

function PastLog({ log, prefix }: { log?: { w?: number; r?: number; rir?: number }; prefix: string }) {
  if (!log || (log.w == null && log.r == null && log.rir == null)) return null
  return (
    <span className="muted" style={{ fontSize: 11.5 }}>
      {prefix}{log.w != null ? `${log.w}` : '?'}×{log.r != null ? `${log.r}` : '?'}{log.rir != null ? ` · RIR ${log.rir}` : ''}
    </span>
  )
}
